import styled, { keyframes, useTheme } from "styled-components/macro";
import type { Assets, GridItemProps } from "../../../../../../types/types.PIM";
import { Fragment, useState, useCallback, useEffect, useMemo } from "react";
import {
  GetDocumentIcon,
  TrimmedName,
  get_content_type,
} from "../util/AssetsUtil";
import moment from "moment";
import {
  GhostButtonMediumV2,
  SecondaryButtonMediumV2,
  TertiaryButtonSmallV2,
} from "../../../../../../components/Buttons/Buttons";
import {
  CaretLeft,
  CaretRight,
  DownloadIcon,
  EditIcon,
  InfoIcon,
  LockIcon,
  NonVisibleIcon,
  UnLockIcon,
  VisibleIcon,
  XIcon,
} from "../../../../../../components/Icons/Icons";
import { useTranslation } from "react-i18next";
import ReactDOM from "react-dom";
import { AssetDocViewer } from "../GridAssetView/AssetDocViewer";
import { formatDate, toTitleCase } from "../../../../../../util/util";
import { SlideOut } from "../../../../../../components/SlideOut/SlideOut";
import { Chips } from "../../../../../../components/Chips/Chips";
import { Chip } from "../../../../../../components/Chips/Chips";
import { spacingTokens } from "../../../../../../designTokens";
import {
  TextMdRegular,
  TextXlRegular,
  TextXsRegular,
} from "../../../../../../components/Typography/TypographyV2";
import { DesktopNotification } from "../../../../../../components/Notifications/Notifications";

const ModalAnimation = keyframes`
 0% { opacity: 0; }
 100% { opacity: 1; }
`;

const CloseIconAnimation = keyframes`
 0% { opacity: 0; transform: rotate(270deg)}
 100% { opacity: 1; transform: rotate(0deg)}
 `;

const CloseButton = styled(GhostButtonMediumV2)`
  animation-name: ${CloseIconAnimation};
  animation-duration: 0.4s;
`;

const GridModalOverlay = styled.div`
  position: fixed;
  display: flex;
  align-items: center;
  flex-direction: column;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.overlayColor};
  animation-name: ${ModalAnimation};
  animation-duration: 0.3s;
  z-index: 900;
  overflow: auto;
  display: flex;
`;

const GridModalHeader = styled.div`
  height: 70px;
  width: 100%;
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  padding-left: 30px;
  background: ${({ theme }) => theme.primaryBG};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const GridModalBody = styled.div`
  height: calc(100% - 70px);
  width: 75%;
  margin-top: 10px;
  background: ${({ theme }) => theme.primaryBG};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ActionButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  width: 100%;
  height: 100%;
  cursor: pointer;
`;

const ButtonWrapper = styled(TertiaryButtonSmallV2)`
  position: absolute;
  top: 50%;
  border-radius: 50%;
  background: ${({ theme }) => theme.primaryBG};
  border: none;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const PreviousButtonWrapper = styled(ButtonWrapper)`
  left: 0;
  margin-left: ${({ theme }) => theme.resolve("spacing_2xl")};
`;

const NextButtonWrapper = styled(ButtonWrapper)`
  right: 0;

  margin-right: ${({ theme }) => theme.resolve("spacing_2xl")};
`;

const ModalAlertBar = styled.div`
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 600px;
  z-index: 900;
  display: flex;
  justify-content: center;
`;

export const DocumentPreview = ({
  assets,
  onCancelPreview,
  onEdit,
  onDownload,
  selected_asset_idx = 0,
  is_product_page = false,
}: {
  assets: (Assets & GridItemProps)[];
  onCancelPreview: () => void;
  onEdit?: (asset: Assets) => void;
  onDownload?: (asset: Assets) => void;
  selected_asset_idx?: number;
  is_product_page?: boolean;
}) => {
  const { t } = useTranslation();
  const [current_asset_idx, set_current_asset_idx] =
    useState(selected_asset_idx);
  const [current_asset, set_current_asset] = useState(
    assets[selected_asset_idx]
  );
  const [show_asset_detail, set_show_asset_detail] = useState(false);
  const [dismissed_asset_id, set_dismissed_asset_id] =
    useState<string | null>(null);

  const on_previous = useCallback(() => {
    if (current_asset_idx > 0) {
      set_current_asset_idx(current_asset_idx - 1);
      set_current_asset(assets[current_asset_idx - 1]);
    }
  }, [assets, current_asset_idx]);

  const on_next = useCallback(() => {
    if (current_asset_idx < assets.length - 1) {
      set_current_asset_idx(current_asset_idx + 1);
      set_current_asset(assets[current_asset_idx + 1]);
    }
  }, [assets, current_asset_idx]);

  const keyupFunction = useCallback(
    (event) => {
      if (event.keyCode === 27) {
        // escape button
        onCancelPreview();
      }
      if (event.keyCode === 37) {
        // left arrow button
        on_previous();
      }
      if (event.keyCode === 39) {
        // right arrow button
        on_next();
      }
    },
    [onCancelPreview, on_next, on_previous]
  );

  const expired_message = useMemo(() => {
    if (!current_asset?.expiration_date) return null;
    if (current_asset.id === dismissed_asset_id) return null;

    try {
      const expirationUTC = moment
        .utc(current_asset.expiration_date)
        .endOf("day");
      if (!expirationUTC.isValid()) return null;

      if (expirationUTC.toDate() >= new Date()) return null;

      return `${t("File expired on")} ${formatDate(
        current_asset.expiration_date
      )}`;
    } catch {
      return null;
    }
  }, [current_asset, dismissed_asset_id, t]);

  useEffect(() => {
    document.addEventListener("keyup", keyupFunction);

    return () => {
      document.removeEventListener("keyup", keyupFunction);
    };
  }, [keyupFunction]);

  useEffect(() => {
    set_current_asset(assets[current_asset_idx]);
  }, [assets, current_asset_idx, selected_asset_idx]);

  const action_items = [
    ...(!!onEdit && current_asset.can_edit
      ? [
          <SecondaryButtonMediumV2 onClick={() => onEdit(current_asset)}>
            <ActionButtonContainer>
              <EditIcon />
              {t("Edit")}
            </ActionButtonContainer>
          </SecondaryButtonMediumV2>,
        ]
      : []),
    ...(!!onDownload && current_asset.asset_type !== "image"
      ? [
          <SecondaryButtonMediumV2 onClick={() => onDownload(current_asset)}>
            <ActionButtonContainer>
              <DownloadIcon />
              {t("Download")}
            </ActionButtonContainer>
          </SecondaryButtonMediumV2>,
        ]
      : []),
    ...(current_asset.can_view_details
      ? [
          <SecondaryButtonMediumV2
            onClick={() => {
              set_show_asset_detail(true);
            }}
          >
            <ActionButtonContainer>
              <InfoIcon />
              {t("Details")}
            </ActionButtonContainer>
          </SecondaryButtonMediumV2>,
        ]
      : []),
  ];

  return ReactDOM.createPortal(
    <GridModalOverlay>
      <GridModalHeader>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: spacingTokens.spacing_lg,
            maxWidth:
              action_items.length === 3
                ? "70%"
                : action_items.length === 2
                ? "78%"
                : "88%",
          }}
        >
          <GetDocumentIcon
            asset_type={current_asset.asset_type}
            file_type={get_content_type(
              current_asset.content_type ?? "application/pdf"
            )}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              maxWidth: "95%",
            }}
          >
            <TrimmedName text={current_asset.name} Content={TextXlRegular} />
            <TextMdRegular style={{ display: "block" }}>
              {current_asset.asset_category}
            </TextMdRegular>
          </div>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          {action_items.map((item, idx) => (
            <Fragment key={idx}>{item}</Fragment>
          ))}
          <CloseButton onClick={onCancelPreview}>
            <XIcon />
          </CloseButton>
        </div>
      </GridModalHeader>

      {expired_message && (
        <ModalAlertBar>
          <DesktopNotification
            text={expired_message}
            notificationType="error"
            removeNotification={() => set_dismissed_asset_id(current_asset.id)}
          />
        </ModalAlertBar>
      )}

      <GridModalBody>
        <AssetDocViewer mode="preview" asset={current_asset} />
      </GridModalBody>
      <PreviousButtonWrapper
        onClick={on_previous}
        disabled={current_asset_idx === 0}
      >
        <CaretLeft />
      </PreviousButtonWrapper>
      <NextButtonWrapper
        onClick={on_next}
        disabled={current_asset_idx === assets.length - 1}
      >
        <CaretRight width={24} height={24} />
      </NextButtonWrapper>
      <SlideOut
        title={t("Asset Details")}
        show={show_asset_detail}
        closeFlyout={() => set_show_asset_detail(false)}
      >
        <ItemDetail asset={current_asset} is_product_page={is_product_page} />
      </SlideOut>
    </GridModalOverlay>,
    document.body
  );
};

const ItemDetail = ({
  asset,
  is_product_page,
}: {
  asset: Assets;
  is_product_page: boolean;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const accessibilityContent = useMemo(() => {
    if (asset.visibility === "internal") {
      return (
        <>
          <div style={{ display: "flex", gap: spacingTokens.spacing_md }}>
            {asset.asset_type === "document" ? (
              <LockIcon />
            ) : (
              <NonVisibleIcon fill={theme.resolve("color_icon_error")} />
            )}
            <TextMdRegular style={{ display: "block" }}>
              {t("Internal Users")}
            </TextMdRegular>
          </div>
          {asset.asset_type === "document" && (
            <AccessibilityChips asset={asset} />
          )}
        </>
      );
    }
    return (
      <>
        <div
          style={{
            display: "flex",
            gap: spacingTokens.spacing_md,
            paddingTop: spacingTokens.spacing_xs,
            alignItems: "center",
          }}
        >
          {asset.asset_type === "document" ? (
            <UnLockIcon
              fill={
                asset.visibility === "logged_in_user"
                  ? theme.resolve("color_icon_caution")
                  : undefined
              }
            />
          ) : (
            <VisibleIcon
              fill={
                asset.visibility === "logged_in_user"
                  ? theme.resolve("color_icon_caution")
                  : asset.is_cover_image
                  ? theme.disabledLinkColor
                  : theme.successIconColor
              }
            />
          )}

          <TextMdRegular style={{ display: "block" }}>
            {asset.visibility === "logged_in_user"
              ? t("Customers & Internal Users")
              : t("Public")}
          </TextMdRegular>
        </div>
        {asset.asset_type === "document" && asset.visibility !== "public" && (
          <AccessibilityChips asset={asset} />
        )}
      </>
    );
  }, [asset, theme, t]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: spacingTokens.spacing_xl,
      }}
    >
      <div>
        <TextXsRegular color="secondary">{t("Asset Name")}</TextXsRegular>
        <TextMdRegular style={{ display: "block" }}>{asset.name}</TextMdRegular>
      </div>
      <div>
        <TextXsRegular color="secondary">{t("Asset Type")}</TextXsRegular>
        <TextMdRegular style={{ display: "block" }}>
          {toTitleCase(asset.asset_type)}
        </TextMdRegular>
      </div>
      <div>
        <TextXsRegular color="secondary">{t("Category")}</TextXsRegular>
        <TextMdRegular style={{ display: "block" }}>
          {asset.asset_category}
        </TextMdRegular>
      </div>
      <div>
        <TextXsRegular color="secondary">{t("Country/Region")}</TextXsRegular>
        <TextMdRegular style={{ display: "block" }}>
          {asset.country}
        </TextMdRegular>
      </div>
      <div>
        <TextXsRegular color="secondary">{t("Language")}</TextXsRegular>
        <TextMdRegular style={{ display: "block" }}>
          {asset.language?.toLocaleUpperCase()}
        </TextMdRegular>
      </div>
      <div>
        <TextXsRegular color="secondary">{t("Issue Date")}</TextXsRegular>
        <TextMdRegular style={{ display: "block" }}>
          {asset.issue_date ? formatDate(asset.issue_date) : "--"}
        </TextMdRegular>
      </div>
      <div>
        <TextXsRegular color="secondary">{t("Expiration Date")}</TextXsRegular>
        <TextMdRegular style={{ display: "block" }}>
          {asset.expiration_date ? formatDate(asset.expiration_date) : "--"}
        </TextMdRegular>
      </div>
      <div>
        <TextXsRegular color="secondary">{t("Renewal Date")}</TextXsRegular>
        <TextMdRegular style={{ display: "block" }}>
          {asset.renewal_date ? formatDate(asset.renewal_date) : "--"}
        </TextMdRegular>
      </div>
      <div>
        <TextXsRegular color="secondary">{t("Last Modified")}</TextXsRegular>
        <TextMdRegular style={{ display: "block" }}>
          {formatDate(asset.modified_at)}
        </TextMdRegular>
      </div>
      <div>
        <TextXsRegular color="secondary">{t("Last Modified By")}</TextXsRegular>
        <TextMdRegular style={{ display: "block" }}>
          {asset.modified_by}
        </TextMdRegular>
      </div>
      <div>
        <TextXsRegular color="secondary">{t("No. of Profiles")}</TextXsRegular>
        <TextMdRegular style={{ display: "block" }}>
          {(asset.number_of_profiles ?? 0) + asset.number_of_products}
        </TextMdRegular>
      </div>
      {is_product_page && (
        <div>
          <TextXsRegular color="secondary">{t("Accessibility")}</TextXsRegular>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: spacingTokens.spacing_md,
              paddingTop: spacingTokens.spacing_xs,
            }}
          >
            {accessibilityContent}
          </div>
        </div>
      )}
    </div>
  );
};

export const AccessibilityChips = ({
  asset,
  changeMap,
  mode,
  showHighlighting = false,
}: {
  asset: Assets;
  changeMap?: Map<string, "added" | "removed" | "unchanged">;
  mode?: "before" | "after";
  showHighlighting?: boolean;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const getChipStyle = useCallback(
    (chipId: string) => {
      if (!showHighlighting || !changeMap) return undefined;
      const changeType = changeMap.get(chipId);
      return {
        backgroundColor:
          changeType === "removed"
            ? theme.resolve("color_bg_surface_error_diff")
            : changeType === "added"
            ? theme.resolve("color_bg_surface_success_diff")
            : undefined,
        marginRight: 6,
        marginBottom: 6,
      };
    },
    [showHighlighting, changeMap, theme]
  );

  const filterChips = useCallback(
    (chips: { name: string; id: string }[]) => {
      if (!showHighlighting || !changeMap || !mode) return chips;
      return chips.filter((chip) => {
        const changeType = changeMap.get(chip.id);
        if (mode === "before") {
          return changeType === "removed" || changeType === "unchanged";
        } else {
          return changeType === "added" || changeType === "unchanged";
        }
      });
    },
    [showHighlighting, changeMap, mode]
  );

  const customersAndDistributors = useMemo(() => {
    let customerChips: { name: string; id: string }[] = [];
    if (asset.allow_all_customers) {
      customerChips.push({
        name: t("All Customers"),
        id: "allow_all_customers",
      });
    }
    if (asset.allow_all_distributors) {
      customerChips.push({
        name: t("All Distributors"),
        id: "allow_all_distributors",
      });
    }
    if (asset.customers?.length) {
      asset.customers?.map((customer) =>
        customerChips.push({
          name: customer.name || customer.tenant_name || "",
          id: customer.id || customer.tenant_id || "",
        })
      );
    }

    const filteredChips = filterChips(customerChips);

    return (
      <>
        <TextMdRegular style={{ display: "block" }}>
          {t("Customers")}
        </TextMdRegular>
        {showHighlighting ? (
          filteredChips.length > 0 ? (
            <div>
              {filteredChips.map((chip) => (
                <Chip
                  key={chip.id}
                  name={chip.name}
                  active={false}
                  onClick={() => {}}
                  style={getChipStyle(chip.id)}
                >
                  {chip.name}
                </Chip>
              ))}
            </div>
          ) : (
            <p>--</p>
          )
        ) : (
          <Chips
            name="customer_chip"
            errors={{}}
            header={""}
            chips={customerChips}
            handleClick={() => {}}
            selectionMode={"single"}
          />
        )}
      </>
    );
  }, [asset, t, showHighlighting, filterChips, getChipStyle]);

  const roles = useMemo(() => {
    let roleChips: { name: string; id: string }[] = [];
    if (asset.allow_all_roles) {
      roleChips.push({ name: t("All Roles"), id: "allow_all_roles" });
    } else {
      asset.roles?.map((role) =>
        roleChips.push({
          name: role.name || "",
          id: role.id,
        })
      );
    }
    const filteredChips = filterChips(roleChips);

    return (
      <>
        <TextMdRegular style={{ display: "block" }}>{t("Roles")}</TextMdRegular>
        {showHighlighting ? (
          filteredChips.length > 0 ? (
            <div>
              {filteredChips.map((chip) => (
                <Chip
                  key={chip.id}
                  name={chip.name}
                  active={false}
                  onClick={() => {}}
                  style={getChipStyle(chip.id)}
                >
                  {chip.name}
                </Chip>
              ))}
            </div>
          ) : (
            <p>--</p>
          )
        ) : (
          <Chips
            name="role_chip"
            errors={{}}
            header={""}
            chips={roleChips}
            handleClick={() => {}}
            selectionMode={"single"}
          />
        )}
      </>
    );
  }, [asset, t, showHighlighting, filterChips, getChipStyle]);

  const users = useMemo(() => {
    let userChips: { name: string; id: string }[] = [];
    if (asset.users?.length) {
      asset.users?.map((user) =>
        userChips.push({
          name: user.fullname || user.firstname + " " + user.lastname,
          id: user.id,
        })
      );
    }
    const filteredChips = filterChips(userChips);
    if (filteredChips.length === 0 && showHighlighting) return null;

    return (
      <>
        <TextMdRegular style={{ display: "block" }}>{t("Users")}</TextMdRegular>
        {filteredChips.length > 0 ? (
          showHighlighting ? (
            <div>
              {filteredChips.map((chip) => (
                <Chip
                  key={chip.id}
                  name={chip.name}
                  active={false}
                  onClick={() => {}}
                  style={getChipStyle(chip.id)}
                >
                  {chip.name}
                </Chip>
              ))}
            </div>
          ) : (
            <Chips
              name="user_chip"
              errors={{}}
              header={""}
              chips={userChips}
              handleClick={() => {}}
              selectionMode={"single"}
            />
          )
        ) : (
          <p>--</p>
        )}
      </>
    );
  }, [asset, t, showHighlighting, filterChips, getChipStyle]);

  return (
    <div>
      {asset.visibility === "logged_in_user" && customersAndDistributors}
      {roles}
      {users}
    </div>
  );
};
